import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Avatar } from '@src/common/components/Avatar';
import { Stack } from '@src/common/components/Stack';
import { StatusBadge } from '@src/common/components/StatusBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { Timestamp } from '@src/common/time/Timestamp';
import type { Note } from '@src/notes/domain/Note';
import { NoteParentType } from '@src/notes/domain/Note';
import { noteStatusColor } from '@src/orders/components/note/NoteStatusColor';
import { useTranslation } from 'react-i18next';

interface Props {
  notes: Note[];
}

export const NoteTable = ({ notes }: Props) => {
  const { t } = useTranslation();
  const { user } = useAuthentication();

  return (
    <Table stickyHeader maxHeight="60vh">
      <TableHead>
        <TableRow>
          <TableCell>{t('date')}</TableCell>
          {!isClientUser(user) && <TableCell>{t('notes.tags')}</TableCell>}
          <TableCell>{t('notes.category')}</TableCell>
          <TableCell>{t('notes.author')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {notes.map((note) => (
          <Row key={note.id} row={note} />
        ))}
      </TableBody>
    </Table>
  );
};

const Row = ({ row }: { row: Note }) => {
  const { t } = useTranslation();
  const { user } = useAuthentication();

  const highlightPermitAction = (text: string) => {
    const regex = /(\(R\)|\(A\)|\(M\))/g;

    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <Typography component="span" key={index} style={{ fontWeight: 600 }}>
          {part}
        </Typography>
      ) : (
        <Typography component="span" key={index}>
          {part}
        </Typography>
      ),
    );
  };

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: theme.palette.primary.primaryContainer + '14', //8% opacity in hex,
        }}
      >
        <TableCell>
          <Typography style={{ fontWeight: 500 }}>
            {Timestamp.from(row.createdAt).formatDate('dd/MM/yyyy h:mm a')}
          </Typography>
        </TableCell>
        {!isClientUser(user) && (
          <TableCell>
            <Stack direction="row" spacing={theme.spacing['2']}>
              {row.tags.map((tag) => (
                <StatusBadge
                  key={tag}
                  radiusFull
                  size="small"
                  text={t(`fieldOptions.${tag}`)}
                  color={noteStatusColor[tag].color}
                  borderColor={noteStatusColor[tag].border}
                  backgroundColor={noteStatusColor[tag].background}
                />
              ))}
            </Stack>
          </TableCell>
        )}
        <TableCell>
          <Typography>
            {row.parent.type === NoteParentType.ORDER
              ? t('order')
              : highlightPermitAction(
                  `${t('notes.permitName')} ${row.parent.name}`,
                )}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack
            direction="row"
            alignItems="center"
            spacing={theme.spacing['2']}
          >
            <Avatar width={24} height={24} />
            <Typography>{row.author}</Typography>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow
        sx={{
          borderBottom: 'none',
          backgroundColor: theme.palette.primary.primaryContainer + '14',
        }}
      >
        <TableCell colSpan={4} sx={{ borderBottom: 'none' }}>
          <Typography style={{ whiteSpace: 'pre-line' }}>
            {row.content}
          </Typography>
        </TableCell>
      </TableRow>
    </>
  );
};
